import { type ChangeEvent, useRef, useState } from "react";
import { FileText, FileUp } from "lucide-react";
import AiScreeningNotice from "@cle/components/AiScreeningNotice";
import CleButton from "@cle/components/CleButton";
import CourseCertificateUploadErrors from "@cle/components/CourseCertificateUploadErrors";
import CleModal, {
  CleModalRoot,
  CleModalTrigger,
} from "@cle/components/CleModal";
import { useUploadInsurancePoliciesBatch } from "@/hooks/useInsurancePolicies";

const InsurancePolicyUploadDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadInsurancePoliciesMutation = useUploadInsurancePoliciesBatch();
  const uploadErrors = uploadInsurancePoliciesMutation.data?.errors ?? [];

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    if (open) {
      uploadInsurancePoliciesMutation.reset();
    }
  };

  const handleChooseFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    if (files.length === 0) {
      return;
    }

    uploadInsurancePoliciesMutation.mutate(files, {
      onSuccess: (result) => {
        if (result.errors.length === 0) {
          setIsOpen(false);
        }
      },
      onSettled: () => {
        event.target.value = "";
      },
    });
  };

  return (
    <CleModalRoot
      controller={{
        open: isOpen,
        setOpen: setIsOpen,
        onOpenChange: handleOpenChange,
      }}
    >
      <CleModalTrigger asChild>
        <CleButton id="cle-upload-insurance-policy" variant="outline" size="sm">
          <FileUp size={17} strokeWidth={2} />
          Upload forsikring
        </CleButton>
      </CleModalTrigger>
      <CleModal
        title="Upload forsikringspolicer"
        description="Upload PDF-forsikringsdokumenter til AI-analyse."
        size="sm"
        footer={
          <div className="d-flex justify-content-center gap-3 w-100 px-3 py-2">
            <CleButton
              id="cle-cancel-insurance-policy-upload"
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={uploadInsurancePoliciesMutation.isPending}
            >
              Afbryd
            </CleButton>
            <CleButton
              id="cle-choose-insurance-policy-files"
              onClick={handleChooseFiles}
              disabled={uploadInsurancePoliciesMutation.isPending}
            >
              {uploadInsurancePoliciesMutation.isPending
                ? "Uploader..."
                : "Vælg filer"}
            </CleButton>
          </div>
        }
      >
        <div className="d-flex flex-column align-items-center text-center gap-3">
          <span
            className="cle-compliance-doc-dashboard__empty-icon mb-0"
            aria-hidden="true"
          >
            <FileText size={24} strokeWidth={1.75} />
          </span>
          <div>
            <h3 className="fs-5 fw-semibold mb-2">
              Vælg dine forsikringspolicer
            </h3>
            <p className="text-muted mb-0">Du kan kun uploade PDF-filer.</p>
          </div>
          <p className="text-muted small mb-0">
            <AiScreeningNotice subject="Forsikringspolicerne" />
          </p>
          {uploadInsurancePoliciesMutation.isError ? (
            <div className="alert alert-warning mb-0 w-100" role="alert">
              Forsikringspolicerne kunne ikke uploades lige nu. Prøv igen.
            </div>
          ) : null}
          {uploadErrors.length > 0 ? (
            <div
              className="alert alert-warning mb-0 w-100 text-start"
              role="alert"
            >
              <CourseCertificateUploadErrors
                errors={uploadErrors}
                title="Nogle forsikringspolicer kunne ikke uploades:"
              />
            </div>
          ) : null}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            className="visually-hidden"
            onChange={handleFilesChange}
          />
        </div>
      </CleModal>
    </CleModalRoot>
  );
};

export default InsurancePolicyUploadDialog;
